import { useEffect, useState } from 'react';
import axios from 'axios';
import MapSelector from './mapSelector';
import ToastNotification from './toastNotification';
import BASE_URL from '../../config';
import './milestoneForm.css';

// Matches milestoneList.js's UK_POSTCODE_RE -- a map-search result's label
// (Nominatim) already ends in its own postcode as part of the formatted
// address text, so if the separate postcode field still has something
// typed into it from before the search, clear it rather than saving two
// different-looking postcodes for the same milestone (see handleMapSelect).
const UK_POSTCODE_RE = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i;

export default function MilestoneForm({ onUidSelect }) {
  const [form, setForm] = useState({ uid: '', name: '', latitude: '', longitude: '', postcode: '', address: '', compass_heading: 0 });
  const [toast, setToast] = useState({ message: '', type: '' });
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [milestones, setMilestones] = useState([]);

 useEffect(() => {
  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('jwt_token');
      const response = await axios.post(
        `${BASE_URL}/api/get_cust_devices/`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDevices(response.data);

      if (response.data.length === 1) {
        const singleUid = response.data[0].uid;
        setForm(form => ({ ...form, uid: singleUid }));

        try {
          const data = await getMilestonesByUid(singleUid);
          setMilestones(data);
          onUidSelect(singleUid);
        } catch (err) {
          console.error('Failed to fetch milestones:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  fetchDevices();
}, [onUidSelect]);



const addMilestone = async (data) => {
  const token = localStorage.getItem('jwt_token');
  const res = await axios.post(`${BASE_URL}/api/add-milestone/`, data, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

const getMilestonesByUid = async (uid) => {
  const token = localStorage.getItem('jwt_token');
  const res = await axios.get(`${BASE_URL}/api/get-milestone-by-uid/`, {
    params: { uid },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const hasCoords = form.latitude !== '' && form.longitude !== '';
    const hasPostcode = form.postcode.trim() !== '';
    if (!hasCoords && !hasPostcode) {
      setToast({ message: 'Search for an address, click the map, or enter a postcode below.', type: 'error' });
      return;
    }

    try {
      await addMilestone(form);
      setToast({ message: 'Milestone added successfully!', type: 'success' });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to add milestone', type: 'error' });
    }
  };

  // `label` is only present when this came from the map's search bar
  // (OpenStreetMap/Nominatim, free and keyless) -- a plain map click has no
  // address text to offer, so the address field is left as whatever the user
  // typed (or blank).
  const handleMapSelect = ({ lat, lng, label }) => {
    setForm(form => ({
      ...form,
      latitude: lat.toFixed(7),
      longitude: lng.toFixed(7),
      address: label || form.address,
      // Clear a stale manually-typed postcode once a search result gives us
      // a full address (which already carries its own postcode) -- avoids
      // saving two conflicting postcodes for the same milestone. A postcode
      // the user typed but hasn't searched with yet (no label) is left alone.
      postcode: label && UK_POSTCODE_RE.test(label) ? '' : form.postcode,
    }));
  };

  const handleDeviceSelect = async (e) => {
    const uid = e.target.value;
    setForm({ ...form, uid });

    if (uid) {
      try {
        const data = await getMilestonesByUid(uid);
        setMilestones(data);
        onUidSelect(uid);
      } catch (err) {
        console.error('Failed to fetch milestones:', err);
      }
    } else {
      setMilestones([]);
      onUidSelect('');
    }
  };

  const closeToast = () => {
    setToast({ message: '', type: '' });
  };

  return (
    <section className="unique-form-container" aria-labelledby="add-milestone-heading">
      <h2 id="add-milestone-heading">Add New Milestone</h2>

      <fieldset className="uid-card">
        <legend className="uid-card-legend">Select Device</legend>
        <label htmlFor="device-select" className="sr-only">Device</label>
        <select
          id="device-select"
          name="uid"
          value={form.uid}
          onChange={handleDeviceSelect}
          required
          className="unique-form-input"
          disabled={loadingDevices}
          aria-busy={loadingDevices}
          aria-describedby={loadingDevices ? 'device-loading-status' : undefined}
        >
          <option value="" disabled>Select Device</option>
          {devices.map(device => (
            <option key={device.uid} value={device.uid}>
              {device.serial}
            </option>
          ))}
        </select>
        {loadingDevices && (
          <p id="device-loading-status" className="sr-only" aria-live="polite">Loading devices...</p>
        )}
      </fieldset>

      <form onSubmit={handleSubmit} className="unique-milestone-form" aria-label="Add milestone">
        <label htmlFor="milestone-name" className="sr-only">Milestone Name</label>
        <input
          id="milestone-name"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Milestone Name"
          required
          className="unique-form-input"
          autoComplete="off"
        />

        <MapSelector onSelect={handleMapSelect} />

        <fieldset className="uid-card">
          <legend className="uid-card-legend">Postcode &amp; Address</legend>
          <p id="postcode-address-help" className="milestone-help-text">
            Only needed if you didn't search or click above: enter a postcode and address and we'll do our best to place it.
          </p>

          <label htmlFor="milestone-postcode" className="sr-only">Postcode</label>
          <input
            id="milestone-postcode"
            name="postcode"
            value={form.postcode}
            onChange={handleChange}
            placeholder="Postcode (e.g. N1 1AA)"
            className="unique-form-input"
            autoComplete="postal-code"
            aria-describedby="postcode-address-help"
          />

          <label htmlFor="milestone-address" className="sr-only">Address</label>
          <input
            id="milestone-address"
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Address (e.g. 12 Main Street)"
            className="unique-form-input"
            autoComplete="street-address"
            aria-describedby="postcode-address-help"
          />
        </fieldset>

        <label htmlFor="milestone-latitude" className="sr-only">Latitude</label>
        <input
          id="milestone-latitude"
          name="latitude"
          value={form.latitude}
          onChange={handleChange}
          placeholder="Latitude (filled in automatically above)"
          className="unique-form-input"
          inputMode="decimal"
        />

        <label htmlFor="milestone-longitude" className="sr-only">Longitude</label>
        <input
          id="milestone-longitude"
          name="longitude"
          value={form.longitude}
          onChange={handleChange}
          placeholder="Longitude (filled in automatically above)"
          className="unique-form-input"
          inputMode="decimal"
        />

        <input
          name="compass_heading"
          value={form.compass_heading}
          onChange={handleChange}
          type="hidden"
        />
        <button type="submit" className="milestone-submit-button">Add Milestone</button>
      </form>

      {toast.message && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}
    </section>
  );
}
